import ArrowLeft from '@mds/icons/ArrowLeft';
import DotsNine from '@mds/icons/DotsNine';
import List from '@mds/icons/List';
import SidebarSimple from '@mds/icons/SidebarSimple';
import type {} from '@mds/ui';
import { themeVars } from '@mds/ui/theme/themeVars';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { CreateSlotsAndSlotProps, SlotProps, useSlot } from '@mds/use-slot';
import {
  appBarClasses,
  Button,
  Divider,
  DrawerProps,
  IconButton,
  IconButtonProps,
  listItemButtonClasses,
  AppBar as MuiAppBar,
  Stack,
  styled,
  SwipeableDrawer,
  SxProps,
  tabClasses,
  Tabs,
  tabsClasses,
  Theme,
  Toolbar,
  Typography,
  useForkRef,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import {
  Children,
  ComponentType,
  CSSProperties,
  forwardRef,
  isValidElement,
  MouseEvent,
  ReactNode,
  RefObject,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { isFragment } from 'react-is';
import { Simplify } from 'type-fest';
import { getUserInitials, UserForAppBar } from '../../utils/userName';
import { AccessoryDrawer } from '../AccessoryDrawer';
import { AccountMenuDrawer, AccountMenuDrawerButton, AppSwitcherAccountMenuItem } from '../AccountMenu';
import { AccountMenuContext } from '../AccountMenu/items/context';
import { useAppLayout } from '../AppLayoutProvider';
import { AppSidebar } from '../AppSidebar';
import { NavigationItemProvider } from '../NavigationItem/context';
import { HomePageLink } from './HomePageLink';

// These won't be exported from @mds/app-layout - consumers should use appBarSize from
// useAppLayout() instead
export const appBarHeightWide = 60;
export const appBarHeightCompact = 48;

const StyledMuiAppBar = styled(MuiAppBar)(({ theme }) => ({
  borderWidth: 0,
  borderBottomWidth: 1,
  borderStyle: 'solid',
  borderColor: themeVars(theme).palette.divider,
  boxShadow: 'none',
  displayPrint: 'none',
  zIndex: theme.zIndex.appBar,
  viewTransitionName: 'app-bar',

  [`&.${appBarClasses.root}`]: {
    backgroundColor: themeVars(theme).palette.background.default,
  },
}));

const StyledToolbar = styled(Toolbar, {
  shouldForwardProp: (prop) => prop !== 'compact' && prop !== 'desktopOnly',
})<{ compact?: boolean; desktopOnly?: boolean }>(({ theme }) => ({
  gap: theme.spacing(1),
  [theme.breakpoints.up('sm')]: {
    gap: theme.spacing(2),
  },
  [theme.breakpoints.up('md')]: {
    gap: theme.spacing(5),
  },
  alignItems: 'stretch',
  minHeight: 'unset',
  height: appBarHeightWide,
  backgroundColor: themeVars(theme).palette.background.default,
  variants: [
    {
      props: { compact: true },
      style: {
        minHeight: 'unset',
        height: appBarHeightCompact,
      },
    },
    {
      props: { desktopOnly: true },
      style: {
        // Edge case (small viewports)
        overflowX: 'auto',
      },
    },
  ],
}));

const ContentContainer = styled(Stack)({
  flex: 1,
  flexDirection: 'row',
  alignItems: 'center',
  [`. ${tabsClasses.scroller}`]: {
    overflowX: 'auto',
  },
});

const ActionContainer = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'compact',
})<{ compact?: boolean }>(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: theme.spacing(2),
  flexWrap: 'nowrap',
  variants: [
    {
      props: { compact: true },
      style: ({ theme }) => ({
        gap: theme.spacing(0),
      }),
    },
  ],
}));

const CompactNavigation = styled(Tabs)(({ theme }) => ({
  variants: [
    {
      props: { orientation: 'vertical' },
      style: {
        minHeight: 'min-content',
        [`& .${tabClasses.root}`]: {
          minHeight: 40,
          // TODO Adjust via props
          marginLeft: -8,
        },
      },
    },
  ],
  // NavigationList already owns its vertical spacing. Keep its horizontal padding
  // from adding a second inset around the compact drawer's full-width rows.
  [`& .${tabsClasses.list} > nav`]: {
    paddingInline: 0,
  },
  [`& .${tabsClasses.list} > .${listItemButtonClasses.root}, & .${tabsClasses.list} > nav > .${listItemButtonClasses.root}`]:
    {
      paddingLeft: theme.spacing(2),
      '--app-navigation-selected-line-width': '4px',
      '--app-navigation-selected-line-color': themeVars(theme).palette.border.focused,
      '--app-navigation-line-top': '50%',
      '--app-navigation-line-bottom': 'auto',
      '--app-navigation-line-height': '1.2em',
      '--app-navigation-line-transform': 'translateY(-50%)',
    },
}));

const LogoContainer = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  flexShrink: 0,
  '& > *': {
    marginRight: theme.spacing(0.5),
    maxHeight: '100%',
  },
  '& *[data-id=MarexChevronSquareRounded]': {
    height: 40,
    width: 'fit-content',
  },
  '& *[data-id=MarexLogo]': {
    height: 32,
    width: 'fit-content',
  },
  '& *[data-id=NeonLogo]': {
    height: 32,
    // Visual alignment (blurred logo)
    marginLeft: -4,
    width: 'fit-content',
  },
}));

const AppTitleContainer = styled('div')({
  overflow: 'hidden',
  minWidth: 0,
  height: '100%',
  display: 'flex',
  flex: '0 1 auto',
  flexWrap: 'wrap',
  alignItems: 'center',
  // We have a few apps with long titles.
  //
  // This is a trick to hide the app title if it doesn't fit into the container on mobile.
  // When the title is too long, it will be flex-wrapped to the second line. But because
  // we set overflow:hidden on this container, the title will become effectively hidden.
  //
  // On mobile, we assume the title is repeated as document <title> anyway.
  '&::before': {
    content: '" "',
    height: '100%',
    display: 'inline-block',
  },
});

const AppTitle = styled(Typography)({
  whiteSpace: 'nowrap',
  userSelect: 'none',
  flexShrink: 0,
  display: 'inline-block',
});

export interface AppBarLayoutSlots {
  /**
   * @default Stack
   */
  actionContainer?: ComponentType<{ compact?: boolean }>;
  /**
   * @default SwipeableDrawer
   */
  drawer?: ComponentType<DrawerProps>;
}

type AppBarLayoutSlotsAndSlotProps = CreateSlotsAndSlotProps<
  AppBarLayoutSlots,
  { actionContainer: SlotProps<'div'>; drawer: SlotProps<typeof SwipeableDrawer> }
>;

export interface AppBarLayoutOwnProps {
  /**
   * Application logo
   *
   * E.g. `NeonLogo` from `@mds/assets` for Neon portal apps or `MarexChevronSquareRounded`
   * from `@mds/assets` for generic Marex apps
   */
  logo?: ReactNode;
  /**
   * Application title
   */
  title?: ReactNode;
  /**
   * Navigation links as a list of <Tab> elements
   *
   * In a layout with a permanent sidebar, this must be undefined.
   */
  navigationItems?: ReactNode;
  /**
   * Actions on the right side of the app bar.
   *
   * To override the account menu button, use the `actionContainer` slot.
   */
  actions?: ReactNode | ((props: { compact: boolean }) => ReactNode);
  /**
   * Content for the built-in app switcher drawer.
   *
   * When provided, an app switcher trigger is shown in the app bar or account menu and
   * opens this content in an accessory drawer.
   */
  appSwitcherItems?: ReactNode;
  /**
   * Icon for the built-in app switcher button.
   */
  appSwitcherIcon?: ReactNode;
  /**
   * Where to show the built-in app switcher trigger.
   *
   * `responsive` (the default) shows the trigger in the app bar on wide layouts. On
   * compact layouts it moves the trigger into the account menu instead, but only when
   * `accountMenuItems` already contains `<AppSwitcherAccountMenuItem />` — detected
   * automatically, no other prop required. Apps that haven't added that component keep
   * the app-bar icon on every layout, so upgrading without touching `accountMenuItems` is
   * always safe and never removes the only trigger the app had.
   *
   * Use `account-menu` when the app bar is too crowded and the trigger should always live
   * in the account menu, even on wide layouts. Use `app-bar` to force the trigger to
   * always stay in the app bar, even on compact layouts where `<AppSwitcherAccountMenuItem />`
   * is present.
   *
   * @default 'responsive'
   */
  appSwitcherPlacement?: 'responsive' | 'app-bar' | 'account-menu';
  /**
   * Props for the built-in app switcher icon button.
   */
  appSwitcherButtonProps?: Omit<IconButtonProps, 'children'>;
  /**
   * `<ListItem>` or `<MenuItem>` elements shown in the account menu (desktop) and in the
   * drawer (mobile)
   */
  accountMenuItems: ReactNode;
  /**
   * User shown in the account menu. Pass in a user object from useAuth0() hook.
   *
   * ```tsx
   * const { user } = useAuth0()
   * <AppBarLayout user={user} ... />
   * ```
   */
  user: UserForAppBar | undefined | null;
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps<Theme>;
  /**
   * Root class name
   */
  className?: string;
  /**
   * Root style
   */
  style?: CSSProperties;
  /*
   * The positioning type. Automatically set if using predefined layouts -
   * `<DashboardLayout>` (static app bar) or `<ScrollableBodyLayout>` (sticky app bar)
   */
  position?: 'fixed' | 'absolute' | 'sticky' | 'static' | 'relative';
  /**
   * Disable sidebar navigation on mobile
   */
  disableSidebar?: boolean;
  /**
   * Show sidebar toggle when compact. Inferred from app layout by default.
   */
  showSidebarToggle?: boolean;
  /**
   * Show right sidebar toggle when compact. Inferred from app layout by default.
   */
  showRightSidebarToggle?: boolean;
  /**
   * Customise URL to the home page. Index route by default ('/').
   *
   * Internally we use app router's Link component which should respect the app base path.
   *
   * @example
   * ```tsx
   * <AppBarLayout homePageLinkProps={{ to: '/dashboard' }} ... />
   * ```
   */
  homePageLinkProps?: Simplify<MdsAdapterLinkProps>;
}

export type AppBarLayoutProps = AppBarLayoutOwnProps & AppBarLayoutSlotsAndSlotProps;

/**
 * Standard app bar layout for Marex apps.
 *
 * Built to support different viewports, sidebar navigations and
 *
 * (!) Do not hardcode the app bar height in your own components. The app bar height
 * changes dynamically based on viewport and it can change in patch releases of MDS - it
 * is not part of the API contract. If you need to observe app bar height in CSS or
 * Javascript, use the `useAppLayout()` utility from @mds/app-layout.
 */
export const AppBarLayout = forwardRef<HTMLDivElement, AppBarLayoutOwnProps & AppBarLayoutSlotsAndSlotProps>(
  function AppBarLayout(props, ref) {
    const {
      accountMenuItems,
      actions,
      className,
      logo,
      title,
      navigationItems: navigationItemsProp,
      position: positionProp,
      appSwitcherButtonProps,
      appSwitcherIcon,
      appSwitcherItems,
      appSwitcherPlacement,
      slotProps,
      slots,
      style,
      sx,
      user,
      disableSidebar,
      showSidebarToggle,
      showRightSidebarToggle,
      homePageLinkProps,
    } = props;

    const { internalAppBarContainerRef, internalAppBarContentNodeRef, appBarVariant } = useAppLayout();

    const combinedRef = useForkRef(ref, internalAppBarContainerRef);

    // <Tabs> from MUI expects an array of <Tab> elements
    const navigationItems = unwrapFragment(navigationItemsProp);

    // Most apps use a horizontal app bar navigation without a permanent apps sidebar. On
    // mobile, we'll move the navigation to a temporary drawer (<AppSidebar>).
    //
    // Apps that don't use a horizontal navigation will have to provide their own
    // <AppSidebar> (can be rendered anywhere in the app).
    const hasDefaultSidebar = !!navigationItems && !disableSidebar;

    // Sticky works with DashboardLayout and ScrollableBodyLayout
    const position = positionProp ?? 'sticky';

    // Conditional JSX is used here for simplicity - we might explore CSS breakpoints in
    // future for better SSR support. For example, the logo element needs to support SVG
    // which cannot be rendered twice on the same page to prevent ID collisions
    // (https://github.com/gregberge/svgr/issues/1500).
    return (
      <StyledMuiAppBar
        position={position}
        ref={combinedRef}
        sx={sx}
        className={className}
        style={style}
        color="inherit"
      >
        <AppBarContent
          appBarVariant={appBarVariant}
          logo={logo}
          title={title}
          navigationItems={navigationItems}
          actions={actions}
          appSwitcherButtonProps={appSwitcherButtonProps}
          appSwitcherIcon={appSwitcherIcon}
          appSwitcherItems={appSwitcherItems}
          appSwitcherPlacement={appSwitcherPlacement}
          accountMenuItems={accountMenuItems}
          user={user}
          contentNodeRef={internalAppBarContentNodeRef}
          hasDefaultSidebar={hasDefaultSidebar}
          showSidebarToggle={showSidebarToggle}
          showRightSidebarToggle={showRightSidebarToggle}
          homePageLinkProps={homePageLinkProps}
          slots={slots}
          slotProps={slotProps}
        />

        {hasDefaultSidebar && <DefaultAppSidebar navigationItems={navigationItems} />}
      </StyledMuiAppBar>
    );
  },
);

function AppBarContent({
  logo,
  title,
  appBarVariant,
  navigationItems,
  actions,
  appSwitcherButtonProps,
  appSwitcherIcon,
  appSwitcherItems,
  appSwitcherPlacement: appSwitcherPlacementProp,
  accountMenuItems,
  user,
  slots,
  slotProps,
  hasDefaultSidebar,
  showSidebarToggle: showSidebarToggleProp,
  showRightSidebarToggle: showRightSidebarToggleProp,
  contentNodeRef,
  homePageLinkProps,
}: {
  logo: AppBarLayoutProps['logo'];
  title: AppBarLayoutProps['title'];
  appBarVariant: 'compact' | 'wide';
  navigationItems: AppBarLayoutProps['navigationItems'];
  actions: AppBarLayoutProps['actions'];
  appSwitcherButtonProps: AppBarLayoutProps['appSwitcherButtonProps'];
  appSwitcherIcon: AppBarLayoutProps['appSwitcherIcon'];
  appSwitcherItems: AppBarLayoutProps['appSwitcherItems'];
  appSwitcherPlacement: AppBarLayoutProps['appSwitcherPlacement'];
  accountMenuItems: AppBarLayoutProps['accountMenuItems'];
  user: AppBarLayoutProps['user'];
  slots: AppBarLayoutProps['slots'];
  slotProps: AppBarLayoutProps['slotProps'];
  hasDefaultSidebar: boolean;
  showSidebarToggle?: boolean;
  showRightSidebarToggle?: boolean;
  contentNodeRef: RefObject<HTMLDivElement | null>;
  homePageLinkProps?: AppBarLayoutProps['homePageLinkProps'];
}) {
  const {
    closeAccessoryDrawers,
    openedAccessoryDrawer,
    toggleAccessoryDrawer,
    toggleSidebar,
    toggleRightSidebar,
    features,
  } = useAppLayout();
  const [ActionContainerSlot, actionContainerProps] = useSlot('actionContainer', {
    elementType: ActionContainer,
    externalForwardedProps: { slots, slotProps },
    ownerState: {},
  });
  const compact = appBarVariant === 'compact';
  const desktopOnly = features.includes('desktopOnly');
  const hasSidebar = hasDefaultSidebar || features.includes('persistentSidebar');
  const showSidebarToggle = showSidebarToggleProp ?? hasSidebar;
  const showRightSidebarToggle = showRightSidebarToggleProp ?? features.includes('persistentRightSidebar');
  const appSwitcherPlacement = appSwitcherPlacementProp ?? 'responsive';
  const hasAppSwitcher = !!appSwitcherItems;
  // `responsive` only moves the trigger into the account menu on compact layouts when the
  // consumer has actually opted in by rendering <AppSwitcherAccountMenuItem /> somewhere in
  // accountMenuItems. This keeps the app-bar icon as the safe, backward-compatible fallback
  // for apps that haven't adopted that component yet, instead of silently losing their only
  // trigger on upgrade.
  const hasAppSwitcherAccountMenuItem =
    hasAppSwitcher &&
    appSwitcherPlacement === 'responsive' &&
    containsElementType(accountMenuItems, AppSwitcherAccountMenuItem);
  const showAppSwitcherInAccountMenu =
    hasAppSwitcher &&
    (appSwitcherPlacement === 'account-menu' ||
      (appSwitcherPlacement === 'responsive' && compact && hasAppSwitcherAccountMenuItem));
  const showAppSwitcherInAppBar = hasAppSwitcher && !showAppSwitcherInAccountMenu;
  const [appSwitcherOpenedFromAccountMenu, setAppSwitcherOpenedFromAccountMenu] = useState(false);
  const [selectedNavigationValue, setSelectedNavigationValue] = useState<unknown>(false);
  const setSelectedNavigationItemValue = useCallback((value: unknown, active: boolean) => {
    if (value === undefined) {
      return;
    }

    setSelectedNavigationValue((currentValue: unknown) => {
      if (active) {
        return value;
      }

      return Object.is(currentValue, value) ? false : currentValue;
    });
  }, []);
  const handleOpenAppSwitcherFromAccountMenu = useCallback(
    (event: MouseEvent) => {
      // Stop this from bubbling to the toolbar's closeAccessoryDrawers handler, which
      // would otherwise close the drawer this click just opened.
      event.stopPropagation();
      setAppSwitcherOpenedFromAccountMenu(true);
      toggleAccessoryDrawer('appSwitcher', true);
    },
    [toggleAccessoryDrawer],
  );
  const handleBackToAccountMenu = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation();
      setAppSwitcherOpenedFromAccountMenu(false);
      toggleAccessoryDrawer('accountMenu', true);
    },
    [toggleAccessoryDrawer],
  );
  const showAppSwitcherBackButton = appSwitcherOpenedFromAccountMenu && openedAccessoryDrawer === 'appSwitcher';
  const accountMenuContextValue = useMemo(
    () => ({
      user: user ?? undefined,
      showAppSwitcherInAccountMenu,
      appSwitcherIcon,
      onOpenAppSwitcherFromAccountMenu: handleOpenAppSwitcherFromAccountMenu,
    }),
    [user, showAppSwitcherInAccountMenu, appSwitcherIcon, handleOpenAppSwitcherFromAccountMenu],
  );

  return (
    <StyledToolbar compact={compact} desktopOnly={desktopOnly} tabIndex={-1} onClick={closeAccessoryDrawers}>
      <Stack
        direction="row"
        sx={{
          gap: 1,
          alignItems: 'center',

          // flex-shrink:1 is required for auto-hiding title in small viewports (see comments in AppTitleContainer)
          // It would look glitchy on small viewports with desktopOnly flag
          flexShrink: desktopOnly ? 0 : 1,

          minWidth: 0,
        }}
      >
        {compact && showSidebarToggle && (
          <IconButton
            color="neutral"
            sx={{ mr: 1, flexShrink: 0 }}
            aria-label="Open drawer"
            edge="start"
            onClick={() => toggleSidebar()}
          >
            <List />
          </IconButton>
        )}

        <HomePageLink
          linkProps={homePageLinkProps}
          sx={{
            display: 'inline-flex',
            flexShrink: 1,
            gap: 1,
            alignItems: 'center',
          }}
        >
          {logo && <LogoContainer>{logo}</LogoContainer>}

          {title && (
            <AppTitleContainer>
              <AppTitle>{title}</AppTitle>
            </AppTitleContainer>
          )}
        </HomePageLink>
      </Stack>
      <ContentContainer>
        {appBarVariant === 'wide' && (
          // `internalAppBarContentNodeRef` must remain on element that is removed from
          // DOM in compact mode - otherwise useOverflowObserver will not work
          <NavigationItemProvider
            orientation="horizontal"
            selectionManagedByTabs
            setSelectedValue={setSelectedNavigationItemValue}
          >
            <Tabs
              ref={contentNodeRef}
              indicator="vertical"
              role="navigation"
              value={selectedNavigationValue}
              orientation="horizontal"
            >
              {navigationItems}
            </Tabs>
          </NavigationItemProvider>
        )}
      </ContentContainer>
      <ActionContainerSlot
        {...actionContainerProps}
        onClick={(event) => {
          // Don't close accessory drawers
          event.stopPropagation();
        }}
      >
        {typeof actions === 'function' ? actions({ compact }) : actions}

        {showAppSwitcherInAppBar && (
          <IconButton
            color="neutral"
            aria-label="Open app switcher"
            aria-controls="accessory-drawer-appSwitcher"
            aria-expanded={openedAccessoryDrawer === 'appSwitcher'}
            {...appSwitcherButtonProps}
            onClick={(event) => {
              appSwitcherButtonProps?.onClick?.(event);
              if (!event.defaultPrevented) {
                setAppSwitcherOpenedFromAccountMenu(false);
                toggleAccessoryDrawer('appSwitcher');
              }
            }}
          >
            {appSwitcherIcon ?? <DotsNine />}
          </IconButton>
        )}

        {compact && showRightSidebarToggle && (
          <IconButton
            color="neutral"
            sx={{ flexShrink: 0 }}
            aria-label="Open right drawer"
            onClick={() => toggleRightSidebar()}
          >
            <SidebarSimple sx={{ transform: 'rotate(180deg)' }} />
          </IconButton>
        )}

        <AccountMenuDrawerButton userInitials={user ? getUserInitials(user) : undefined} />
      </ActionContainerSlot>
      {/* eslint-disable-next-line react/no-context-provider */}
      <AccountMenuContext.Provider value={accountMenuContextValue}>
        <AccountMenuDrawer>{accountMenuItems}</AccountMenuDrawer>
      </AccountMenuContext.Provider>
      {appSwitcherItems && (
        <>
          {/*
            Always mounted, unlike the app bar icon and account menu item that can
            trigger this drawer, so the drawer's aria-labelledby reference never dangles.
          */}
          <Typography id="accessory-drawer-appSwitcher-trigger" sx={visuallyHidden}>
            App switcher
          </Typography>
          <AccessoryDrawer drawerKey="appSwitcher">
            {showAppSwitcherBackButton && (
              <>
                <Stack sx={{ px: 1, py: 1, alignItems: 'flex-start' }}>
                  <Button variant="text" color="secondary" startIcon={<ArrowLeft />} onClick={handleBackToAccountMenu}>
                    Back
                  </Button>
                </Stack>
                <Divider />
              </>
            )}
            {appSwitcherItems}
          </AccessoryDrawer>
        </>
      )}
    </StyledToolbar>
  );
}

function DefaultAppSidebar({ navigationItems }: { navigationItems: AppBarLayoutProps['navigationItems'] }) {
  const { toggleSidebar, appBarVariant } = useAppLayout();
  return (
    <AppSidebar
      ModalProps={{
        keepMounted: appBarVariant === 'compact', // better open performance on mobile
      }}
    >
      <Stack
        sx={{
          gap: 0,
          height: '100%',
          py: 2,
        }}
      >
        <CompactNavigation
          indicator="vertical"
          role="navigation"
          value={false}
          orientation="vertical"
          onClick={() => toggleSidebar()}
          variant="standard"
        >
          {navigationItems}
        </CompactNavigation>
      </Stack>
    </AppSidebar>
  );
}

function unwrapFragment(children: ReactNode) {
  if (isFragment(children)) {
    return children.props.children;
  }
  return children;
}

// Recursively searches a ReactNode tree (fragments, arrays, and nested elements included) for
// an element of the given component type, without requiring it to be a direct child.
function containsElementType(node: ReactNode, type: ComponentType<never>): boolean {
  let found = false;
  // `Children.forEach` (rather than `Array.isArray`) is required here: `node` may be a
  // single element, a Fragment, an array, or a mix, and this needs to walk all of those
  // shapes uniformly at every level of the recursion.
  // eslint-disable-next-line react/no-children-for-each
  Children.forEach(node, (child) => {
    if (found || !isValidElement(child)) {
      return;
    }
    if (child.type === type) {
      found = true;
      return;
    }
    if (containsElementType((child.props as { children?: ReactNode }).children, type)) {
      found = true;
    }
  });
  return found;
}
